import React, { useState, useEffect } from 'react';
import {
  X, Clock, CheckCircle2, ArrowRight, Users,
  Video, Brain, Zap, Phone
} from 'lucide-react';

// Inflated count - add this to actual count for display
const WAITLIST_INFLATE = 33;

const exclusiveFeatures = [
  {
    icon: Video,
    title: "AI Meeting Bot",
    description: "Your AI clone joins calls, presents, and responds - while you focus on what matters.",
    gradient: "from-blue-500 to-cyan-500",
    iconBg: "bg-blue-500/10"
  },
  {
    icon: Brain,
    title: "Knowledge Graph AI",
    description: "Connected to Gmail, Slack, Notion. Your AI knows everything and takes intelligent action.",
    gradient: "from-purple-500 to-pink-500",
    iconBg: "bg-purple-500/10"
  },
  {
    icon: Zap,
    title: "Smart Automation",
    description: "Execute powerful commands with natural language. Schedule meetings, send emails, draft messages.",
    gradient: "from-amber-500 to-orange-500",
    iconBg: "bg-amber-500/10"
  }
];

export default function WaitingListModal({ isOpen, onClose, darkMode }) {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ totalCount: 0, userPosition: null });

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consentToContact, setConsentToContact] = useState(false);

  const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

  // Fetch waitlist stats on open
  useEffect(() => {
    if (isOpen) {
      // Pre-fill email and name from user data first
      let userEmail = '';
      let userName = '';
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          userEmail = user.email || '';
          userName = user.name || '';
          setEmail(userEmail);
          setName(userName);
        } catch (e) { }
      }

      // Only fetch status if we have an email
      if (userEmail) {
        fetch(`${API_BASE_URL}/auth/waitlist/status?email=${encodeURIComponent(userEmail)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
          .then(res => res.json())
          .then(data => {
            console.log('Waitlist status response:', data); // Debug log

            // Update count - handle different API response formats
            const count = data.totalCount ?? data.total ?? data.count ?? 0;
            setStats({
              totalCount: count,
              userPosition: data.userPosition ?? data.position ?? null
            });

            // Check if user is already on waitlist - handle different API response formats
            if (data.userPosition || data.position || data.isJoined || data.joined) {
              setJoined(true);
            }
          })
          .catch(err => console.error('Failed to fetch waitlist stats', err));
      }
    }
  }, [isOpen, API_BASE_URL]);

  const handleJoinWaitlist = async () => {
    // Validation
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!phone.trim()) {
      setError('Phone number is required');
      return;
    }
    // Phone validation - at least 10 digits
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid phone number (at least 10 digits)');
      return;
    }
    if (!consentToContact) {
      setError('Please agree to receive updates from our team');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_BASE_URL}/auth/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ name, email, phone })
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.message === 'Email already on waitlist') {
          setJoined(true);
          setStats(prev => ({ ...prev, userPosition: data.position }));
        } else {
          throw new Error(data.message || 'Failed to join waitlist');
        }
      } else {
        setJoined(true);
        setStats({
          totalCount: data.totalCount || stats.totalCount + 1,
          userPosition: data.position
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-rotate features
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % exclusiveFeatures.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isOpen]);

  if (!isOpen) return null;

  const currentFeat = exclusiveFeatures[currentFeature];
  const displayCount = stats.totalCount + WAITLIST_INFLATE;
  const totalSeats = 100;
  const spotsRemaining = Math.max(0, totalSeats - displayCount);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300 overflow-y-auto">
      <div className={`relative w-full max-w-5xl my-auto rounded-3xl shadow-2xl overflow-hidden ${darkMode ? 'bg-slate-900' : 'bg-white'}`}>

        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-3 sm:top-4 right-3 sm:right-4 z-20 p-2 rounded-full transition-all hover:rotate-90 ${darkMode ? 'hover:bg-white/10 text-white/70 hover:text-white' : 'hover:bg-black/5 text-black/50 hover:text-black'}`}
        >
          <X size={20} className="sm:w-6 sm:h-6" />
        </button>

        <div className="flex flex-col lg:flex-row">

          {/* LEFT SIDE: Features Showcase */}
          <div className={`w-full lg:w-3/5 p-6 sm:p-8 lg:p-10 relative overflow-hidden ${darkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>

            {/* Feature Card - Animated */}
            <div className="relative min-h-[240px] sm:min-h-[280px]">
              {exclusiveFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ${index === currentFeature
                    ? 'opacity-100 translate-y-0'
                    : index < currentFeature
                      ? 'opacity-0 -translate-y-6'
                      : 'opacity-0 translate-y-6'
                    }`}
                >
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mb-4 sm:mb-5 ${feature.iconBg}`}>
                    <feature.icon className={`w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r ${feature.gradient} bg-clip-text`} style={{ color: 'transparent', background: `linear-gradient(to right, var(--tw-gradient-stops))`, WebkitBackgroundClip: 'text' }} />
                    <feature.icon className={`w-7 h-7 sm:w-8 sm:h-8 text-current`} style={{ color: feature.gradient.includes('blue') ? '#3B82F6' : feature.gradient.includes('purple') ? '#A855F7' : '#F59E0B' }} />
                  </div>

                  <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3 sm:mb-4 leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {feature.title}
                  </h2>

                  <p className={`text-base sm:text-lg leading-relaxed max-w-lg ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Feature Indicators */}
            <div className="relative z-10 flex items-center justify-between mt-4">
              <div className="flex gap-2">
                {exclusiveFeatures.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentFeature(index)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${index === currentFeature
                      ? `w-8 bg-gradient-to-r ${currentFeat.gradient}`
                      : `w-2 ${darkMode ? 'bg-white/20' : 'bg-slate-300'}`
                      }`}
                  />
                ))}
              </div>

              <div className={`text-xs font-mono ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {currentFeature + 1} / {exclusiveFeatures.length}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Form */}
          <div className={`w-full lg:w-2/5 p-6 sm:p-8 lg:p-10 flex flex-col justify-center ${darkMode ? 'bg-slate-900' : 'bg-white'}`}>

            <div className="mb-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 text-red-500 border border-red-500/20 mb-4">
                <Clock size={14} className="animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-wider">High Demand</span>
              </div>

              <h3 className={`text-2xl sm:text-3xl font-extrabold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Join the Waitlist
              </h3>

              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Reserve your spot for early access.
              </p>
            </div>

            {!joined ? (
              <>
                {/* Form Fields */}
                <div className="space-y-3 mb-4">
                  <input
                    type="text"
                    placeholder="Your Name *"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border text-sm transition-all ${darkMode
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:border-teal-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-teal-500'
                      } focus:outline-none focus:ring-2 focus:ring-teal-500/20`}
                  />

                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={email}
                    readOnly
                    disabled
                    className={`w-full px-4 py-3 rounded-xl border text-sm cursor-not-allowed ${darkMode
                      ? 'bg-slate-700 border-slate-600 text-slate-300 placeholder-slate-500'
                      : 'bg-slate-100 border-slate-200 text-slate-600 placeholder-slate-400'
                      }`}
                  />

                  <div className="relative">
                    <Phone className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                    <input
                      type="tel"
                      placeholder="Phone Number *"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm transition-all ${darkMode
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:border-teal-500'
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-teal-500'
                        } focus:outline-none focus:ring-2 focus:ring-teal-500/20`}
                    />
                  </div>

                  {/* Consent Checkbox - Mandatory */}
                  <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${consentToContact
                    ? (darkMode ? 'bg-teal-500/10 border-teal-500/30' : 'bg-teal-50 border-teal-200')
                    : (darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200')
                    }`}>
                    <input
                      type="checkbox"
                      checked={consentToContact}
                      onChange={(e) => setConsentToContact(e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-slate-300 text-teal-500 focus:ring-teal-500"
                    />
                    <span className={`text-xs leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      I agree to receive calls/emails from the HiCapy team about new features and to provide feedback. <span className="text-red-400">*</span>
                    </span>
                  </label>
                </div>

                {/* Stats Card */}
                <div className={`p-4 rounded-xl mb-4 border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Users className={`w-4 h-4 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
                      <span className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Spots Filling Fast
                      </span>
                    </div>
                    <span className="font-mono font-bold text-teal-500">
                      {displayCount}
                    </span>
                  </div>

                  <div className={`w-full h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-all duration-1000"
                      style={{ width: `${Math.min((displayCount / totalSeats) * 100, 100)}%` }}
                    />
                  </div>

                  <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    {spotsRemaining} spots remaining
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleJoinWaitlist}
                  disabled={loading}
                  className="w-full py-4 rounded-xl font-bold text-base bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 hover:from-teal-500 hover:via-cyan-500 hover:to-blue-500 text-white shadow-lg shadow-teal-500/30 transition-all active:scale-[0.98] flex items-center justify-center gap-2 group"
                >
                  {loading ? (
                    <span className="animate-pulse">Joining...</span>
                  ) : (
                    <>
                      Secure My Spot
                      <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </>
            ) : (
              /* Success State */
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-500/10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  You're on the list!
                </h4>
                {stats.userPosition && (
                  <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    Your position: <span className="font-bold text-teal-500">#{stats.userPosition + WAITLIST_INFLATE}</span>
                  </p>
                )}
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  We'll notify you when it's your turn!
                </p>
              </div>
            )}

            {error && (
              <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-red-500 text-sm text-center font-medium">{error}</p>
              </div>
            )}

            {/* Trust indicators */}
            <div className="mt-4 flex items-center justify-center gap-2 text-xs">
              <CheckCircle2 className={`w-3.5 h-3.5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                No credit card required
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}